import { FC, useState } from 'react';
import { Button } from '@cb/apricot-react';
import { CircularProgress, Grid } from '@mui/material';
import { useAuth } from 'src/contexts';
import { useToasts } from 'src/hooks';
import { buildReport } from 'src/utils';
import type { ReportType, RecordCategoryType } from 'src/utils';
import './styles.css';

const Reports: FC = () => {
  const { roles } = useAuth();
  const { render: renderToasts, toastErrorMessage } = useToasts();
  const [downloading, setDownloading] = useState<string | null>(null);

  const isCodeServicesVisible =
    roles.includes('XPM_ADMIN') || roles.includes('CODE_CONTROL') || roles.includes('QUALITY_CONTROL');

  const handleDownload = async (reportType: ReportType, codeType: string) => {
    const key = `${reportType}-${codeType}`;
    const recordCategoryType: RecordCategoryType = codeType === 'schoolCode' ? 'ai' : 'di';
    try {
      setDownloading(key);
      await buildReport(reportType, recordCategoryType);
    } catch (error: any) {
      toastErrorMessage(error.message);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="reports-page">
      {renderToasts()}
      <h5 style={{ textAlign: 'center' }}>
        Please select the report you would like to download:
      </h5>
      <br />
      <Grid container spacing={4} justifyContent="center">
        {/* Application Status Column - accessible for REVIEWER, CODE_CONTROL, XPM_ADMIN, and QUALITY_CONTROL */}
        <Grid item xs={12} sm={4}>
          <div className="reports-column">
            <h6 className="reports-column-header">Application Status</h6>
              <p className="reports-column-description">
                Report used to track application status and programs the applying organization is interested in.
                <br />Report Range: 1 year from the download date.
              </p>
            <div className="reports-button-group">
              <Button
                className="reports-button"
                disabled={downloading === 'applicationStatus-scoreSendCode'}
                onClick={() => handleDownload('applicationStatus', 'scoreSendCode')}
              >
                Score Send Code
                {downloading === 'applicationStatus-scoreSendCode' && (
                  <CircularProgress size={16} style={{ marginLeft: 8 }} />
                )}
              </Button>
              <Button
                className="reports-button"
                disabled={downloading === 'applicationStatus-schoolCode'}
                onClick={() => handleDownload('applicationStatus', 'schoolCode')}
              >
                School Code
                {downloading === 'applicationStatus-schoolCode' && (
                  <CircularProgress size={16} style={{ marginLeft: 8 }} />
                )}
              </Button>
            </div>
          </div>
        </Grid>

        {/* Access Code Column  - accessible for REVIEWER, CODE_CONTROL, XPM_ADMIN, and QUALITY_CONTROL */}
        <Grid item xs={12} sm={4}>
          <div className="reports-column">
            <h6 className="reports-column-header">Access Code</h6>
              <p className="reports-column-description">
                Report used to generate access codes to organizations that have newly assigned codes.
                <br />Report Range: 1 year from the download date.
              </p>
            <div className="reports-button-group">
              <Button
                className="reports-button"
                disabled={downloading === 'accessCode-scoreSendCode'}
                onClick={() => handleDownload('accessCode', 'scoreSendCode')}
              >
                Score Send Code
                {downloading === 'accessCode-scoreSendCode' && (
                  <CircularProgress size={16} style={{ marginLeft: 8 }} />
                )}
              </Button>
              <Button
                className="reports-button"
                disabled={downloading === 'accessCode-schoolCode'}
                onClick={() => handleDownload('accessCode', 'schoolCode')}
              >
                School Code
                {downloading === 'accessCode-schoolCode' && (
                  <CircularProgress size={16} style={{ marginLeft: 8 }} />
                )}
              </Button>
            </div>
          </div>
        </Grid>

        {/* Code Services Column — XPM_ADMIN,QUALITY_CONTROL and CODE_CONTROL only */}
        {isCodeServicesVisible && (
          <Grid item xs={12} sm={4}>
            <div className="reports-column">
              <h6 className="reports-column-header">Code Services</h6>
              <p className="reports-column-description">
                Report used by Code Services to assign work and track quality metrics.
                <br />Report Range: Current and previous admin year from the download date.
              </p>
              <div className="reports-button-group">
                <Button
                  className="reports-button"
                  disabled={downloading === 'codeServices-scoreSendCode'}
                  onClick={() => handleDownload('codeServices', 'scoreSendCode')}
                >
                  Score Send Code
                  {downloading === 'codeServices-scoreSendCode' && (
                    <CircularProgress size={16} style={{ marginLeft: 8 }} />
                  )}
                </Button>
                <Button
                  className="reports-button"
                  disabled={downloading === 'codeServices-schoolCode'}
                  onClick={() => handleDownload('codeServices', 'schoolCode')}
                >
                  School Code
                  {downloading === 'codeServices-schoolCode' && (
                    <CircularProgress size={16} style={{ marginLeft: 8 }} />
                  )}
                </Button>
              </div>
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default Reports;
